import { createPortal } from 'react-dom';
import { useMemo } from 'react';

import { SelectOption, Account } from '~/shared/utils/types';
import FilterButton from '~/shared/components/filters/FilterButton';
import { usePanelBehavior } from '~/shared/utils/usePanelBehavior';

import { useFilterState } from './hooks';
import FilterPanelHeader from './FilterPanelHeader';
import FilterPanelContent from './FilterPanelContent';

export type GroupReportselectionOption = {
  label: string;
  options: SelectOption[];
};

interface ReportsFiltersSidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onResetFilters: () => void;
  // Applied filters passed from parent
  appliedClusterNameFilters: SelectOption[];
  appliedInstanceFilters: SelectOption[];
  appliedVolumeIdFilters: SelectOption[];
  appliedVolumeTagFilters: SelectOption[];
  appliedAccountFilters: SelectOption[];
  appliedAutoscaleState: string[];
  appliedEligibility: string[];
  activeFilterCount: number;
  setSidePanelOpen: (open: boolean) => void;
  isSidePanelOpen: boolean;
  isOrgView?: boolean;
  accounts?: Account[];
  activeOrgId?: string;
}

export default function ReportsFiltersSidePanel({
  open,
  onOpenChange,
  onResetFilters,
  appliedClusterNameFilters,
  appliedInstanceFilters,
  appliedVolumeIdFilters,
  appliedVolumeTagFilters,
  appliedAccountFilters,
  appliedAutoscaleState,
  appliedEligibility,
  activeFilterCount,
  setSidePanelOpen,
  isSidePanelOpen,
  isOrgView = false,
  accounts = [],
  activeOrgId,
}: ReportsFiltersSidePanelProps) {
  const panelId = 'reports-filters-panel';

  const filterableAccounts = useMemo(() => {
    if (!activeOrgId) {
      return accounts;
    }
    return accounts.filter((account) => account.accountId !== activeOrgId);
  }, [accounts, activeOrgId]);

  const filterState = useFilterState({
    open,
    appliedClusterNameFilters,
    appliedInstanceFilters,
    appliedVolumeIdFilters,
    appliedVolumeTagFilters,
    appliedAccountFilters,
    appliedAutoscaleState,
    appliedEligibility,
  });

  const hasActiveFilters =
    // appliedClusterNameFilters.length > 0 ||
    // appliedInstanceFilters.length > 0 ||
    // appliedVolumeIdFilters.length > 0 ||
    // appliedVolumeTagFilters.length > 0 ||
    // appliedAutoscaleState.length > 0 ||
    filterState.pendingClusterNameFilters.length > 0 ||
    filterState.pendingInstanceFilters.length > 0 ||
    filterState.pendingVolumeIdFilters.length > 0 ||
    filterState.pendingVolumeTagFilters.length > 0 ||
    filterState.pendingAccountFilters.length > 0 ||
    filterState.pendingAutoscaleState.length > 0 ||
    filterState.pendingEligibility.length > 0;

  const handleClose = () => {
    filterState.applyPendingFilters();
  };

  const { panelRef } = usePanelBehavior(open, onOpenChange, true, undefined, handleClose);

  const handleResetFilters = () => {
    filterState.resetPendingFilters();
    onResetFilters();
    onOpenChange(false);
  };

  const panel = (
    <div
      id={panelId}
      ref={panelRef}
      className={`fixed -mt[0.125px] right-0 z-[2000] w-[587px] bg-white border-l border-primary-70 transform transition-transform duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'
        }`}
      aria-label="Reports filters"
      style={{
        top: 0,
        bottom: 0,
        boxShadow: '-15px 86px 57.4px -3px rgba(0, 0, 0, 0.09)',
      }}
    >
      <div className="flex flex-col h-full">
        <FilterPanelHeader
          onClearAll={handleResetFilters}
          hasActiveFilters={hasActiveFilters}
          onClose={() => {
            handleClose();
            onOpenChange(false);
          }}
        />

        <FilterPanelContent
          panelOpen={open}
          pendingAutoscaleState={filterState.pendingAutoscaleState}
          setPendingAutoscaleState={filterState.setPendingAutoscaleState}
          pendingEligibility={filterState.pendingEligibility}
          setPendingEligibility={filterState.setPendingEligibility}
          pendingClusterNameFilters={filterState.pendingClusterNameFilters}
          setPendingClusterNameFilters={filterState.setPendingClusterNameFilters}
          pendingInstanceFilters={filterState.pendingInstanceFilters}
          setPendingInstanceFilters={filterState.setPendingInstanceFilters}
          pendingVolumeIdFilters={filterState.pendingVolumeIdFilters}
          setPendingVolumeIdFilters={filterState.setPendingVolumeIdFilters}
          pendingVolumeTagFilters={filterState.pendingVolumeTagFilters}
          setPendingVolumeTagFilters={filterState.setPendingVolumeTagFilters}
          pendingAccountFilters={filterState.pendingAccountFilters}
          setPendingAccountFilters={filterState.setPendingAccountFilters}
          isOrgView={isOrgView}
          accounts={filterableAccounts}
        />
      </div>
    </div>
  );

  return (
    <>
      <div className="flex gap-2 items-cente justify-end  w-full max-w-[713px] bg-gree">
        <FilterButton
          activeFilterCount={activeFilterCount}
          onOpen={() => setSidePanelOpen(!isSidePanelOpen)}
          onClearAll={handleResetFilters}
          size="md-responsive"
          variant="third"
          aria-expanded={isSidePanelOpen}
          aria-controls={panelId}
        />
      </div>
      {typeof document !== 'undefined' ? createPortal(panel, document.body) : null}
    </>
  );
}
